import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Learning } from '@shared/entities/learning';
import { FsAttachmentService } from '@shared/services/api/fs-attachment.service';
import { ProfileService } from '@shared/services/api/profile.service';
import { Roles } from '@shared/models/types';
import { take, takeUntil, Subject } from 'rxjs';

@Component({
  selector: 'app-learning-card',
  templateUrl: './learning-card.component.html',
  styleUrls: ['./learning-card.component.scss']
})
export class LearningCardComponent implements OnInit, OnDestroy {
  @Input() learning!: Learning;

  imageUrl = '';
  imageLoaded = false;
  isAdmin = false;
  private objectUrl?: string;
  private destroy$ = new Subject<void>();

  constructor(
    private _fsAttachmentService: FsAttachmentService,
    private _profileService: ProfileService,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadPreviewImage();

    this._profileService.role$.pipe(takeUntil(this.destroy$)).subscribe((role) => {
      this.isAdmin = role === Roles.ADMIN;
    });
  }

  ngOnDestroy(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadPreviewImage(): void {
    const attachmentId = this.learning.preview?.id || this.learning.preview?.attachmentId;

    if (!attachmentId) {
      this.imageUrl = 'assets/img/default-learning-preview.jpg';
      this.imageLoaded = true;
      return;
    }

    this._fsAttachmentService
      .getAttachmentUrl(attachmentId)
      .pipe(take(1))
      .subscribe({
        next: (blob) => {
          this.objectUrl = URL.createObjectURL(blob);
          this.imageUrl = this.objectUrl;
          this.imageLoaded = true;
          this._cdr.markForCheck();
        },
        error: () => {
          this.imageUrl = 'assets/img/default-learning-preview.jpg';
          this.imageLoaded = true;
        }
      });
  }

  onImageLoad(): void {
    this.imageLoaded = true;
  }

  onImageError(): void {
    if (this.imageUrl !== 'assets/img/default-learning-preview.jpg') {
      this.imageUrl = 'assets/img/default-learning-preview.jpg';
    }
    this.imageLoaded = true;
  }

  getComplexityLevel(): number {
    if (!this.learning.complexity) return 0;

    const complexityMap: Record<string, number> = {
      BEGINNER: 1,
      INTERMEDIATE: 2,
      ADVANCED: 3
    };

    return complexityMap[this.learning.complexity.toUpperCase()] || 0;
  }

  getComplexityArray(): number[] {
    const level = this.getComplexityLevel();
    return Array(3)
      .fill(0)
      .map((_, i) => (i < level ? 1 : 0));
  }

  getFormattedTags(): string {
    if (!this.learning.tags || this.learning.tags.length === 0) return '';
    return this.learning.tags.map((tag) => `#${tag}`).join(' | ');
  }
}
